import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox,
  Divider,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Iconify } from '../../components/iconify';
import { getPromotionStockByType } from './promotionStockHelpers';

const selectMenuProps = {
  disableScrollLock: true,
  PaperProps: {
    sx: {
      maxHeight: 300,
      overflow: 'auto',
      '& .MuiList-root': {
        paddingTop: '8px',
        paddingBottom: '8px',
      },
    },
  },
};

const buildMenuProps = (custom = {}) => ({
  ...selectMenuProps,
  ...custom,
  PaperProps: {
    ...(selectMenuProps.PaperProps || {}),
    ...(custom.PaperProps || {}),
    sx: {
      ...(selectMenuProps.PaperProps?.sx || {}),
      ...(custom.PaperProps?.sx || {}),
    },
  },
});

export default function BuyXGetYFree({
  formData,
  handleInputChange,
  handleOpenProductModal,
  handleRemoveProduct,
  handleProductCompulsoryChange,
  handleProductQuantityChange,
  handleProductTypeChange,
  handleProductPricingChange,
  buyProductsSearch,
  setBuyProductsSearch,
  getProductsSearch,
  setGetProductsSearch,
  errors = {},
  readOnly = false,
  isViewMode = false,
}) {
  const getProductTypeOptions = (split) => {
    if (split === 0) return [{ value: 'Box', label: 'Box' }];
    if (split === 1) return [{ value: 'Piece', label: 'Piece' }];
    if (split === 2)
      return [
        { value: 'Box', label: 'Box' },
        { value: 'Piece', label: 'Piece' },
      ];
    return [{ value: 'Box', label: 'Box' }];
  };

  const getDefaultProductType = (split) => {
    if (split === 0) return 'Box';
    if (split === 1) return 'Piece';
    if (split === 2) return 'Box';
    return 'Box';
  };

  const normalizeTypeValue = (type, split) => {
    if (!type || type === '' || type === 'undefined' || type === 'null' || type === 'Both') {
      return getDefaultProductType(split);
    }
    return type;
  };

  const getSelectedProductType = (product) => {
    return normalizeTypeValue(product.type, product.split);
  };

  const parsePrice = (value) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
  };

  const getPriceByType = (product, mode = 'sales') => {
    const selectedType = getSelectedProductType(product);
    if (selectedType === 'Piece') {
      if (mode === 'offer') {
        return parsePrice(
          product.pieceOfferPrice ??
          product.offerPiecePrice ??
          product.offerPrice ??
          0
        );
      }
      return parsePrice(
        product.pieceSalesPrice ??
        product.piece_sales_price ??
        product.PiecePrice ??
        product.salesPrice ??
        product.price ??
        0
      );
    }
    if (mode === 'offer') {
      return parsePrice(
        product.boxOfferPrice ??
        product.offerBoxPrice ??
        product.offerPrice ??
        0
      );
    }
    return parsePrice(
      product.boxSalesPrice ??
      product.salesPrice ??
      product.price ??
      0
    );
  };

  const formatPrice = (value) => parsePrice(value).toFixed(2);

  const getStockByType = (product) => {
    if (!product) return 0;
    const currentType = product.type || getSelectedProductType(product);
    const selectedType = currentType === 'Piece' || currentType === 'piece' ? 'Piece' :
      currentType === 'Box' || currentType === 'box' ? 'Box' :
        getSelectedProductType(product);
    return getPromotionStockByType(product, selectedType);
  };

  useEffect(() => {
    const buyProductsNeedUpdate = formData.buyProducts.some(product => {
      const normalizedType = normalizeTypeValue(product.type, product.split);
      const hasValidPricing = product.pricingType && product.pricingType !== '' && product.pricingType !== 'undefined' && product.pricingType !== 'null';
      return product.type !== normalizedType || !hasValidPricing;
    });

    if (buyProductsNeedUpdate) {
      const updatedProducts = formData.buyProducts.map(product => {
        const normalizedType = normalizeTypeValue(product.type, product.split);
        const hasValidPricing = product.pricingType && product.pricingType !== '' && product.pricingType !== 'undefined' && product.pricingType !== 'null';

        if (product.type !== normalizedType || !hasValidPricing) {
          return {
            ...product,
            type: normalizedType,
            pricingType: hasValidPricing ? product.pricingType : 'sales',
          };
        }
        return product;
      });
      handleInputChange('buyProducts', updatedProducts);
    }
  }, [formData.buyProducts]);

  useEffect(() => {
    const getProductsNeedUpdate = formData.getProducts.some(product => {
      const normalizedType = normalizeTypeValue(product.type, product.split);
      const hasValidPricing = product.pricingType && product.pricingType !== '' && product.pricingType !== 'undefined' && product.pricingType !== 'null';
      return product.type !== normalizedType || !hasValidPricing;
    });

    if (getProductsNeedUpdate) {
      const updatedProducts = formData.getProducts.map(product => {
        const normalizedType = normalizeTypeValue(product.type, product.split);
        const hasValidPricing = product.pricingType && product.pricingType !== '' && product.pricingType !== 'undefined' && product.pricingType !== 'null';

        if (product.type !== normalizedType || !hasValidPricing) {
          return {
            ...product,
            type: normalizedType,
            pricingType: hasValidPricing ? product.pricingType : 'sales',
          };
        }
        return product;
      });
      handleInputChange('getProducts', updatedProducts);
    }
  }, [formData.getProducts]);

  const filteredBuyProducts = formData.buyProducts
    .map((product, index) => ({
      ...product,
      type: getSelectedProductType(product),
      pricingType: product.pricingType || 'sales',
      originalIndex: index
    }))
    .filter((product) => {
      const searchLower = buyProductsSearch.toLowerCase();
      return (
        product.name.toLowerCase().includes(searchLower) ||
        (product.code && product.code.toLowerCase().includes(searchLower)) ||
        product.id.toString().toLowerCase().includes(searchLower)
      );
    });

  const filteredGetProducts = formData.getProducts
    .map((product, index) => ({
      ...product,
      type: getSelectedProductType(product),
      pricingType: product.pricingType || 'sales',
      originalIndex: index
    }))
    .filter((product) => {
      const searchLower = getProductsSearch.toLowerCase();
      return (
        product.name.toLowerCase().includes(searchLower) ||
        (product.code && product.code.toLowerCase().includes(searchLower)) ||
        product.id.toString().toLowerCase().includes(searchLower)
      );
    });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, width: '100%' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#003DA5', fontSize: '2rem' }}>
          Buy X Get Y Free Configuration
        </Typography>

        <Tooltip
          title={
            <Box sx={{ p: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6, fontSize: '0.6rem' }}>
                • When products are marked as compulsory, customer must buy minimum 1 of each
                compulsory product
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6, fontSize: '0.6rem' }}>
                • Total of compulsory products should be greater than or equal to total buy quantity
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6, fontSize: '0.6rem' }}>
                • If offer price is '0', system will use sales price automatically
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.6, fontSize: '0.6rem' }}>
                • Partial quantities only get one promotion cycle (Buy 7 in "Buy 5 Get 2" = Get 2
                free, not 2.8)
              </Typography>
            </Box>
          }
          arrow
          placement="right"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: '#1e3a5f',
                color: 'white',
                fontSize: '0.465rem',
                maxWidth: 500,
                boxShadow: 2,
                '& .MuiTooltip-arrow': {
                  color: '#1e3a5f',
                },
              },
            },
          }}
        >
          <IconButton size="small" sx={{ color: '#003DA5' }}>
            <Iconify icon="eva:info-fill" />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider color="black" />

      <Grid container spacing={2} mt={0.1}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, fontSize: '1rem' }}>
            Buy Products
          </Typography>

          <TextField
            className="input-border text-field-promo"
            fullWidth
            label={
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Total Buy Quantity<span style={{ color: 'red' }}>*</span>
              </span>
            }
            value={formData.buyQuantity}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                handleInputChange('buyQuantity', value);
              }
            }}
            onKeyDown={(e) => {
              if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                e.preventDefault();
              }
            }}
            placeholder="Enter total buy quantity"
            type="number"
            inputProps={{ min: 1, max: 9999 }}
            InputProps={{
              readOnly: readOnly,
              sx: { height: 40 },
            }}
            onWheel={(e) => e.target.blur()}
            error={!!errors.buyQuantity}
            helperText={errors.buyQuantity}
            sx={{
              mb: 1,
              '& input[type=number]': { MozAppearance: 'textfield' },
              '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              }
            }}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              my: 1,
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                Selected Products ({formData.buyProducts.length})
              </Typography>
              {errors.buyProducts && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.2 }}>
                  {errors.buyProducts}
                </Typography>
              )}
            </Box>
            {!readOnly && (
              <Button
                variant="outlined"
                className="button-variant-outlined"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={() => handleOpenProductModal('buy')}
                disabled={formData.buyProducts.length >= 200}
                disableRipple
              >
                Add More Products
              </Button>
            )}
          </Box>
          {formData.buyProducts.length > 0 && (
            <Box sx={{ mt: 1, maxHeight: 400, overflowY: 'auto', pr: 1 }}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  bgcolor: 'background.paper',
                  pb: 1,
                  mb: 1,
                  borderBottom: '1px solid',
                  borderColor: 'rgba(59, 59, 59, 0.45)',
                }}
              >
                <TextField
                  className="allow-interaction input-border"
                  fullWidth
                  placeholder="Search by product name or product code"
                  value={buyProductsSearch}
                  onChange={(e) => setBuyProductsSearch(e.target.value)}
                  InputProps={{
                    className: 'allow-interaction',
                    startAdornment: (
                      <InputAdornment position="start" >
                        <Iconify icon="eva:search-fill" sx={{ color: '#212b36' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Stack spacing={2}>
                {filteredBuyProducts.map((product) => (
                  <Card
                    key={`${product.id}-${product.originalIndex}`}
                    sx={{ border: 1, borderColor: 'rgba(59, 59, 59, 0.54)', boxShadow: 1 }}
                  >
                    <CardContent sx={{ px: 2, py: 1, '&:last-child': { pb: 1 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                              mb: 0.5,
                              fontSize: '0.80rem',
                              color: '#003DA5',
                            }}
                          >
                            {product.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '0.75rem', mb: 0.5, color: '#212b36' }}
                          >
                            Code: {product.code} | Stock: {getStockByType(product)}
                          </Typography>
                        </Box>
                        {!readOnly && (
                          <IconButton
                            onClick={() =>
                              handleRemoveProduct(`${product.id}-${product.originalIndex}`, 'buy')
                            }
                            size="small"
                            color="error"
                          >
                            <Iconify icon="solar:close-circle-outline" />
                          </IconButton>
                        )}
                      </Box>

                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={product.compulsory ? 3 : 4}>
                          <FormControl fullWidth size="small" error={!!errors[`buyProduct_${product.originalIndex}_type`]}>
                            <InputLabel className="commonColor">Product Type</InputLabel>
                            <Select
                              className="input-border"
                              value={getSelectedProductType(product)}
                              label="Product Type"
                              onChange={(e) =>
                                handleProductTypeChange(
                                  `${product.id}-${product.originalIndex}`,
                                  'buy',
                                  e.target.value
                                )
                              }
                              MenuProps={buildMenuProps()}
                              disabled={readOnly}
                            >
                              {getProductTypeOptions(product.split || 2).map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors[`buyProduct_${product.originalIndex}_type`] && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                {errors[`buyProduct_${product.originalIndex}_type`]}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={product.compulsory ? 4 : 4}>
                          <FormControl fullWidth size="small" error={!!errors[`buyProduct_${product.originalIndex}_pricing`] || !!errors[`buyProduct_${product.originalIndex}_offerPrice`]}>
                            <InputLabel>Pricing</InputLabel>
                            <Select
                              className="input-border"
                              value={(product.pricingType && product.pricingType !== 'undefined' && product.pricingType !== 'null') ? product.pricingType : 'sales'}
                              label="Pricing"
                              onChange={(e) =>
                                handleProductPricingChange(
                                  `${product.id}-${product.originalIndex}`,
                                  'buy',
                                  e.target.value
                                )
                              }
                              MenuProps={buildMenuProps()}
                              disabled={readOnly}
                            >
                              <MenuItem value="sales" sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                Standard
                              </MenuItem>
                              <MenuItem value="offer" sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                Offer
                              </MenuItem>
                            </Select>
                            {(errors[`buyProduct_${product.originalIndex}_pricing`] || errors[`buyProduct_${product.originalIndex}_offerPrice`]) && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                {errors[`buyProduct_${product.originalIndex}_pricing`] || errors[`buyProduct_${product.originalIndex}_offerPrice`]}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={product.compulsory ? 3 : 4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={product.compulsory}
                                onChange={
                                  readOnly
                                    ? undefined
                                    : (e) =>
                                      handleProductCompulsoryChange(
                                        `${product.id}-${product.originalIndex}`,
                                        'buy',
                                        e.target.checked
                                      )
                                }
                                disabled={readOnly}
                                sx={{
                                  color: '#F8991D',
                                  '&.Mui-checked': {
                                    color: '#F8991D',
                                  },
                                }}
                              />
                            }
                            label="Compulsory"
                          />
                        </Grid>
                        {product.compulsory && (
                          <Grid item xs={12} sm={2}>
                            <TextField
                              fullWidth
                              size="small"
                              className="input-border"
                              label={
                                <span>
                                  Qty <span style={{ color: 'red' }}>*</span>
                                </span>
                              }
                              value={product.quantity ?? ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (
                                  value === '' ||
                                  (/^\d+$/.test(value) && parseInt(value) <= 9999)
                                ) {
                                  handleProductQuantityChange(
                                    `${product.id}-${product.originalIndex}`,
                                    'buy',
                                    e.target.value
                                  );
                                }
                              }}
                              onKeyDown={(e) => {
                                if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              type="number"
                              InputProps={{ readOnly: readOnly }}
                              inputProps={{ min: 1, max: 9999 }}
                              onWheel={(e) => e.target.blur()}
                              error={!!errors[`buyProduct_${product.originalIndex}_quantity`]}
                              helperText={errors[`buyProduct_${product.originalIndex}_quantity`]}
                              sx={{
                                '& input[type=number]': { MozAppearance: 'textfield' },
                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                                },
                              }}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, fontSize: '1rem' }}>
            Get Products (Free)
          </Typography>

          <TextField
            fullWidth
            className="input-border text-field-promo"
            label={
              <span>
                Total Get Quantity<span style={{ color: 'red' }}>*</span>
              </span>
            }
            value={formData.getQuantity}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                handleInputChange('getQuantity', value);
              }
            }}
            onKeyDown={(e) => {
              if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                e.preventDefault();
              }
            }}
            placeholder="Enter total get quantity"
            type="number"
            inputProps={{ min: 1, max: 9999 }}
            InputProps={{
              readOnly: readOnly,
              sx: { height: 40 },
            }}
            onWheel={(e) => e.target.blur()}
            error={!!errors.getQuantity}
            helperText={errors.getQuantity}
            sx={{
              mb: 1,
              '& input[type=number]': { MozAppearance: 'textfield' },
              '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              }
            }}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              my: 1,
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                Selected Products ({formData.getProducts.length})
              </Typography>
              {errors.getProducts && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {errors.getProducts}
                </Typography>
              )}
            </Box>
            {!readOnly && (
              <Button
                variant="outlined"
                className="button-variant-outlined"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={() => handleOpenProductModal('get')}
                disabled={formData.getProducts.length >= 200}
                disableRipple
              >
                Add More Products
              </Button>
            )}
          </Box>
          {formData.getProducts.length > 0 && (
            <Box sx={{ mt: 1, mb: 2, maxHeight: 400, overflowY: 'auto', pr: 1 }}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  bgcolor: 'background.paper',
                  pb: 1,
                  mb: 1,

                  borderBottom: '1px solid',
                  borderColor: 'rgba(59, 59, 59, 0.45)',
                }}
              >
                <TextField
                  className="allow-interaction input-border"
                  fullWidth
                  placeholder="Search by product name or product code"
                  value={getProductsSearch}
                  onChange={(e) => setGetProductsSearch(e.target.value)}
                  InputProps={{
                    className: 'allow-interaction',
                    startAdornment: (
                      <InputAdornment position="start" >
                        <Iconify icon="eva:search-fill" sx={{ color: '#212b36' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Stack spacing={2}>
                {filteredGetProducts.map((product) => (
                  <Card
                    key={`${product.id}-${product.originalIndex}`}
                    sx={{ border: 1, borderColor: 'rgba(59, 59, 59, 0.54)', boxShadow: 1 }}
                  >
                    <CardContent sx={{ px: 2, py: 1, '&:last-child': { pb: 1 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                              mb: 0.5,
                              fontSize: '0.80rem',
                              color: '#003DA5',
                            }}
                          >
                            {product.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '0.75rem', mb: 0.5, color: '#212b36' }}
                          >
                            Code: {product.code} | Stock: {getStockByType(product)}
                          </Typography>
                        </Box>
                        {!readOnly && (
                          <IconButton
                          onClick={() =>
                            handleRemoveProduct(
                              `${product.id}-${product.originalIndex}`,
                              'get'
                            )
                          }
                          size="small"
                          color="error"
                        >
                            <Iconify icon="solar:close-circle-outline" />
                          </IconButton>
                        )}
                      </Box>

                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={product.compulsory ? 3 : 4}>
                          <FormControl fullWidth size="small" error={!!errors[`getProduct_${product.originalIndex}_type`]}>
                            <InputLabel className="commonColor">Product Type</InputLabel>
                            <Select
                              className="input-border"
                              value={getSelectedProductType(product)}
                              label="Product Type"
                              onChange={(e) =>
                                handleProductTypeChange(
                                  `${product.id}-${product.originalIndex}`,
                                  'get',
                                  e.target.value
                                )
                              }
                              MenuProps={buildMenuProps()}
                              disabled={readOnly}
                            >
                              {getProductTypeOptions(product.split || 2).map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors[`getProduct_${product.originalIndex}_type`] && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                {errors[`getProduct_${product.originalIndex}_type`]}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={product.compulsory ? 4 : 4}>
                          <FormControl fullWidth size="small" error={!!errors[`getProduct_${product.originalIndex}_pricing`]}>
                            <InputLabel>Pricing</InputLabel>
                            <Select
                              className="input-border"
                              value={(product.pricingType && product.pricingType !== 'undefined' && product.pricingType !== 'null') ? product.pricingType : 'sales'}
                              label="Pricing"
                              onChange={(e) =>
                                handleProductPricingChange(
                                  `${product.id}-${product.originalIndex}`,
                                  'get',
                                  e.target.value
                                )
                              }
                              MenuProps={buildMenuProps()}
                              disabled={readOnly}
                            >
                              <MenuItem value="sales" sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                Standard
                              </MenuItem>
                              <MenuItem value="offer" sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                Offer
                              </MenuItem>
                            </Select>
                            {errors[`getProduct_${product.originalIndex}_pricing`] && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                {errors[`getProduct_${product.originalIndex}_pricing`]}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={product.compulsory ? 3 : 4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={product.compulsory}
                                onChange={
                                  readOnly
                                    ? undefined
                                    : (e) =>
                                      handleProductCompulsoryChange(
                                        `${product.id}-${product.originalIndex}`,
                                        'get',
                                        e.target.checked
                                      )
                                }
                                disabled={readOnly}
                                sx={{
                                  color: '#F8991D',
                                  '&.Mui-checked': {
                                    color: '#F8991D',
                                  },
                                }}
                              />
                            }
                            label="Compulsory"
                          />
                        </Grid>
                        {product.compulsory && (
                          <Grid item xs={12} sm={2}>
                            <TextField
                              fullWidth
                              size="small"
                              className="input-border"
                              label={
                                <span>
                                  Qty <span style={{ color: 'red' }}>*</span>
                                </span>
                              }
                              value={product.quantity ?? ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (
                                  value === '' ||
                                  (/^\d+$/.test(value) && parseInt(value) <= 9999)
                                ) {
                                  handleProductQuantityChange(
                                    `${product.id}-${product.originalIndex}`,
                                    'get',
                                    e.target.value
                                  );
                                }
                              }}
                              onKeyDown={(e) => {
                                if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              type="number"
                              inputProps={{ min: 1, max: 9999 }}
                              InputProps={{ readOnly: readOnly }}
                              onWheel={(e) => e.target.blur()}
                              error={!!errors[`getProduct_${product.originalIndex}_quantity`]}
                              helperText={errors[`getProduct_${product.originalIndex}_quantity`]}
                              sx={{
                                '& input[type=number]': { MozAppearance: 'textfield' },
                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                                },
                              }}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
